import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineGrid,
  Badge,
  DataTable,
  Button,
  Icon,
  Box,
  Select,
  InlineStack,
  Spinner,
  Popover,
  TextField,
  useBreakpoints,
  OptionList,
  Scrollable,
  DatePicker,
  Tooltip as ToolTip,
  Toast,
  Frame,
  Divider,
} from "@shopify/polaris";
import * as XLSX from "xlsx";
import {
  ArrowUpIcon,
  ArrowDownIcon,
  ExportIcon,
  InfoIcon,
  CalendarIcon,
  ArrowRightIcon,
  WandIcon,
  LightbulbIcon,
  PlusIcon,
  CheckIcon,
  ClockIcon,
} from "@shopify/polaris-icons";
import Chart from "react-apexcharts";
import axios from "axios";
import {
  Link,
  useNavigate,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  closestCorners,
  MouseSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { EditIcon, ResetIcon } from "@shopify/polaris-icons";
import OptmisedContent from "../Modals/OptmisedContent";
import HeadlineModal from "../Modals/HeadlineModal";
import IdeasModal from "../Modals/IdeasModal";
import ArticlePreviewIdeasModal from '../Modals/ArticlePreviewIdeasModal';


// Helper function for formatting dates (from attached file)
function formatDateToYearMonthDayDateString(date) {
  const year = String(date.getFullYear());
  let month = String(date.getMonth() + 1);
  let day = String(date.getDate());
  if (month.length < 2) month = "0" + month;
  if (day.length < 2) day = "0" + day;
  return [year, month, day].join("-");
}

// Custom header for Polaris DatePicker (multiMonth) (from attached file)
function MonthHeader({ month, year, onPrev, onNext }) {
  const left = new Date(year, month, 1);
  const right = new Date(year, month + 1, 1);

  const leftLabel = left.toLocaleString("en-US", { month: "long" });
  const rightLabel = right.toLocaleString("en-US", { month: "long" });

  return (
    <div className="dp-header">
      <span className="dp-label">
        {leftLabel} — {rightLabel}
      </span>
    </div>
  );
}

// DateRangePicker component (adapted from attached file)
function DateRangePicker({
  initialDateRange,
  onChange,
  disabled = false,
  initialPopoverActive = false,
}) {
  const { mdDown, lgUp } = useBreakpoints();
  const shouldShowMultiMonth = lgUp;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const ranges = [
    {
      title: "Last 7 days",
      alias: "last7days",
      period: {
        since: new Date(
          new Date(new Date().setDate(today.getDate() - 7)).setHours(0, 0, 0, 0)
        ),
        until: today,
      },
    },
    {
      title: "Last 30 days",
      alias: "last30days",
      period: {
        since: new Date(
          new Date(new Date().setDate(today.getDate() - 30)).setHours(
            0,
            0,
            0,
            0
          )
        ),
        until: today,
      },
    },
    {
      title: "Last 90 days",
      alias: "last90days",
      period: {
        since: new Date(
          new Date(new Date().setDate(today.getDate() - 90)).setHours(
            0,
            0,
            0,
            0
          )
        ),
        until: today,
      },
    },
  ];

  // Initialize with the prop value
  const [popoverActive, setPopoverActive] = useState(initialPopoverActive);
  const [activeDateRange, setActiveDateRange] = useState(
    initialDateRange || ranges[1]
  );
  const [inputValues, setInputValues] = useState({});
  const [{ month, year }, setDate] = useState({
    month: activeDateRange.period.since.getMonth(),
    year: activeDateRange.period.since.getFullYear(),
  });

  const datePickerRef = useRef(null);
  const VALID_YYYY_MM_DD_DATE_REGEX = /^\d{4}-\d{1,2}-\d{1,2}/;

  useEffect(() => {
    if (popoverActive) {
      const timer = setTimeout(() => {
        const titles = document.querySelectorAll(".Polaris-DatePicker__Title");
        titles.forEach((title) => {
          const text = title.textContent.trim();
          if (/\s\d{4}$/.test(text)) {
            title.textContent = text.slice(0, -5);
          }
        });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [popoverActive, month, year]);

  function isDate(date) {
    return !isNaN(new Date(date).getDate());
  }

  function isValidYearMonthDayDateString(date) {
    return VALID_YYYY_MM_DD_DATE_REGEX.test(date) && isDate(date);
  }

  function isValidDate(date) {
    return date.length === 10 && isValidYearMonthDayDateString(date);
  }

  function parseYearMonthDayDateString(input) {
    const [year, month, day] = input.split("-");
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  function formatDate(date) {
    return formatDateToYearMonthDayDateString(date);
  }

  function nodeContainsDescendant(rootNode, descendant) {
    if (rootNode === descendant) return true;
    let parent = descendant.parentNode;
    while (parent != null) {
      if (parent === rootNode) return true;
      parent = parent.parentNode;
    }
    return false;
  }

  function isNodeWithinPopover(node) {
    return datePickerRef?.current
      ? nodeContainsDescendant(datePickerRef.current, node)
      : false;
  }

  function handleStartInputValueChange(value) {
    setInputValues((prevState) => ({ ...prevState, since: value }));
    if (isValidDate(value)) {
      const newSince = parseYearMonthDayDateString(value);
      setActiveDateRange((prevState) => {
        const newPeriod =
          prevState.period && newSince <= prevState.period.until
            ? { since: newSince, until: prevState.period.until }
            : { since: newSince, until: newSince };
        return { ...prevState, period: newPeriod };
      });
    }
  }

  function handleEndInputValueChange(value) {
    setInputValues((prevState) => ({ ...prevState, until: value }));
    if (isValidDate(value)) {
      const newUntil = parseYearMonthDayDateString(value);
      setActiveDateRange((prevState) => {
        const newPeriod =
          prevState.period && newUntil >= prevState.period.since
            ? { since: prevState.period.since, until: newUntil }
            : { since: newUntil, until: newUntil };
        return { ...prevState, period: newPeriod };
      });
    }
  }

  function handleInputBlur({ relatedTarget }) {
    const isRelatedTargetWithinPopover =
      relatedTarget != null && isNodeWithinPopover(relatedTarget);

    // Check if the related target is part of the DatePicker calendar
    const isDatePickerElement =
      relatedTarget &&
      (relatedTarget.closest(".Polaris-DatePicker") ||
        relatedTarget.closest(".Polaris-Popover__Pane") ||
        relatedTarget.closest('[role="grid"]')); // DatePicker uses role="grid"

    if (isRelatedTargetWithinPopover || isDatePickerElement) return;

    setPopoverActive(false);
  }

  function handleCalendarChange({ start, end }) {
    const newDateRange = ranges.find(
      (range) =>
        range.period.since.valueOf() === start.valueOf() &&
        range.period.until.valueOf() === end.valueOf()
    ) || {
      alias: "custom",
      title: "Custom Range",
      period: { since: start, until: end },
    };
    setActiveDateRange(newDateRange);
  }

  function apply() {
    if (onChange) {
      const diffTime = Math.abs(
        activeDateRange.period.until.getTime() -
          activeDateRange.period.since.getTime()
      );
      const days = Math.round(diffTime / (1000 * 60 * 60 * 24));
      onChange({ period: activeDateRange.period, days: days.toString() });
    }
    setPopoverActive(false);
  }

  function cancel() {
    setPopoverActive(false);
  }

  useEffect(() => {
    if (activeDateRange) {
      setInputValues({
        since: formatDate(activeDateRange.period.since),
        until: formatDate(activeDateRange.period.until),
      });
      function monthDiff(referenceDate, newDate) {
        return (
          newDate.month -
          referenceDate.month +
          12 * (referenceDate.year - newDate.year)
        );
      }
      const monthDifference = monthDiff(
        { year, month },
        {
          year: activeDateRange.period.until.getFullYear(),
          month: activeDateRange.period.until.getMonth(),
        }
      );
      if (monthDifference > 1 || monthDifference < 0) {
        setDate({
          month: activeDateRange.period.until.getMonth(),
          year: activeDateRange.period.until.getFullYear(),
        });
      }
    }
  }, [activeDateRange]);

  const buttonValue =
    activeDateRange.alias === "custom"
      ? `${activeDateRange.period.since.toDateString()} - ${activeDateRange.period.until.toDateString()}`
      : activeDateRange.title;

  return (
    <Popover
      active={popoverActive}
      autofocusTarget="none"
      preferredAlignment="left"
      preferredPosition="below"
      fluidContent
      sectioned={false}
      fullHeight
      activator={
        <Button
          size="slim"
          onClick={() => setPopoverActive(!popoverActive)}
          disabled={disabled}
        >
          {buttonValue}
        </Button>
      }
      onClose={() => setPopoverActive(false)}
    >
      <Popover.Pane fixed>
        <InlineGrid
          columns={{ xs: "1fr", mdDown: "1fr", md: "max-content max-content" }}
          gap={0}
          ref={datePickerRef}
        >
          <Box
            maxWidth={mdDown ? "516px" : "212px"}
            width={mdDown ? "100%" : "212px"}
            padding={{ xs: 500, md: 0 }}
            paddingBlockEnd={{ xs: 100, md: 0 }}
          >
            {mdDown ? (
              <Select
                label="dateRangeLabel"
                labelHidden
                onChange={(value) => {
                  const result = ranges.find(
                    ({ title, alias }) => title === value || alias === value
                  );
                  setActiveDateRange(result);
                }}
                value={activeDateRange?.title || activeDateRange?.alias || ""}
                options={ranges.map(({ alias, title }) => title || alias)}
              />
            ) : (
              <Scrollable style={{ height: "334px" }}>
                <OptionList
                  options={ranges.map((range) => ({
                    value: range.alias,
                    label: range.title,
                  }))}
                  selected={[activeDateRange.alias]}
                  onChange={(value) => {
                    setActiveDateRange(
                      ranges.find((range) => range.alias === value[0])
                    );
                  }}
                />
              </Scrollable>
            )}
          </Box>
          <Box padding={{ xs: 500 }} maxWidth={mdDown ? "320px" : "516px"}>
            <BlockStack gap="400">
              <InlineStack gap="200">
                <div style={{ flexGrow: 1 }}>
                  <TextField
                    role="combobox"
                    label={"Since"}
                    labelHidden
                    prefix={<Icon source={CalendarIcon} />}
                    value={inputValues.since}
                    onChange={handleStartInputValueChange}
                    onBlur={handleInputBlur}
                    autoComplete="off"
                  />
                </div>
                <Icon source={ArrowRightIcon} />
                <div style={{ flexGrow: 1 }}>
                  <TextField
                    role="combobox"
                    label={"Until"}
                    labelHidden
                    prefix={<Icon source={CalendarIcon} />}
                    value={inputValues.until}
                    onChange={handleEndInputValueChange}
                    onBlur={handleInputBlur}
                    autoComplete="off"
                  />
                </div>
              </InlineStack>
              <div className="dp-with-header">
                <MonthHeader
                  month={month}
                  year={year}
                  onPrev={() => {
                    const prev = new Date(year, month - 1, 1);
                    setDate({
                      month: prev.getMonth(),
                      year: prev.getFullYear(),
                    });
                  }}
                  onNext={() => {
                    const next = new Date(year, month + 1, 1);
                    setDate({
                      month: next.getMonth(),
                      year: next.getFullYear(),
                    });
                  }}
                />
                <DatePicker
                  selected={{
                    start: activeDateRange.period.since,
                    end: activeDateRange.period.until,
                  }}
                  onChange={handleCalendarChange}
                  allowRange
                  month={month}
                  year={year}
                  multiMonth
                  onMonthChange={(m, y) => setDate({ month: m, year: y })}
                />
              </div>
            </BlockStack>
          </Box>
        </InlineGrid>
      </Popover.Pane>
      <Popover.Pane fixed>
        <Popover.Section>
          <InlineStack align="end">
            <div style={{ display: "flex", flexDirection: "row", gap: "8px" }}>
              <Button onClick={cancel}>Cancel</Button>
              <Button variant="primary" onClick={apply}>
                Apply
              </Button>
            </div>
          </InlineStack>
        </Popover.Section>
      </Popover.Pane>
    </Popover>
  );
}

function SortableCard({
  id,
  editMode,
  children,
  className = "",
  style: customStyle,
  draggingHeight,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !editMode });

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition: isDragging
      ? "none"
      : "transform 0.2s ease, box-shadow 0.2s ease",
    zIndex: isDragging ? 100 : "auto",
    background: "white",
    borderRadius: "8px",
    position: "relative",
    opacity: isDragging ? 0.5 : 1,
    height:
      isDragging && draggingHeight[id] ? `${draggingHeight[id]}px` : undefined,
    cursor: editMode ? (isDragging ? "grabbing" : "grab") : "default",
    ...customStyle,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`sortable-card ${editMode ? "is-edit" : ""} ${className}`}
      data-dragging={isDragging}
      data-id={id}
      {...attributes}
      {...listeners}
    >
      {children}
    </div>
  );
}

function ArticleDetail({ shopOrigin, apiKey }) {
  const location = useLocation();
  const { articleId: rawArticleId } = useParams();
  const { smDown, mdDown } = useBreakpoints();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams(); // ✅ ADD THIS

  // Parse the article_id from URL parameter
  // URL format: /article/article_id=610390540587
  const articleId = useMemo(() => {
    if (rawArticleId && rawArticleId.includes("=")) {
      return rawArticleId.split("=")[1];
    }
    return rawArticleId;
  }, [rawArticleId]);

  // ✅ Extract filters from URL query parameters
  const categoryFromUrl = searchParams.get("category") || "all";
  const sinceFromUrl = searchParams.get("since");
  const untilFromUrl = searchParams.get("until");
  // NEW: Metrics State
  const [metricsData, setMetricsData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState("30");
  const [shopDomain, setShopDomain] = useState(null);
  const [articlesList, setArticlesList] = useState([]);
  // const [selectedArticle, setSelectedArticle] = useState("all");
  const [blogsList, setBlogsList] = useState([]);
  // const [selectedCategory, setSelectedCategory] = useState("all");
  const [currentDatePeriod, setCurrentDatePeriod] = useState(null);
  // Add state for insights
  const [insights, setInsights] = useState({
    performance_insights: [],
    content_optimization: [],
    strategic_actions: [],
  });
  // const [insightsLoading, setInsightsLoading] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [topPostsBySessions, setTopPostsBySessions] = useState([]);
  const [topPostsSessionsCount, setTopPostsSessionsCount] = useState("5");
  // const [dateRangeSessions, setDateRangeSessions] = useState({
  //   since: new Date(new Date().setDate(new Date().getDate() - 30)),
  //   until: new Date(),
  // });

  // Top 5 Engagements State
  const [engagementLoading, setEngagementLoading] = useState(false);
  const [topEngagementData, setTopEngagementData] = useState([]);
  const [topEngagementCount, setTopEngagementCount] = useState("5");
  // const [dateRangeEngagement, setDateRangeEngagement] = useState({
  //   since: new Date(new Date().setDate(new Date().getDate() - 30)),
  //   until: new Date(),
  // });

  // Top Clicked Products State
  const [topProductsLoading, setTopProductsLoading] = useState(false);
  const [topProductsData, setTopProductsData] = useState([]);
  const [topProductsCount, setTopProductsCount] = useState(5);
  // const [dateRangeProducts, setDateRangeProducts] = useState({
  //   since: new Date(new Date().setDate(new Date().getDate() - 30)),
  //   until: new Date(),
  // });

  // Add state for chart data
  const [chartData, setChartData] = useState({
    "last 7 days": [],
    "last 30 days": [],
    "last 90 days": [],
    custom: [],
  });
  const [activeTab, setActiveTab] = useState("Last 30 days");

  const [chartLoading, setChartLoading] = useState(false);
  // const [chartDateRange, setChartDateRange] = useState({
  //   since: new Date(new Date().setDate(new Date().getDate() - 30)),
  //   until: new Date(),
  // });

  // Add chart tab state (Views vs Engagement)
  const [chartActiveMetric, setChartActiveMetric] = useState("views"); // 'views' or 'engagement'

  // Store engagement data separately
  const [engagementChartData, setEngagementChartData] = useState({
    "last 7 days": [],
    "last 30 days": [],
    "last 90 days": [],
    custom: [],
  });

  // Loading state for engagement data
  const [engagementChartLoading, setEngagementChartLoading] = useState(false);

  // Global Date range state
  const [globalDateRange, setGlobalDateRange] = useState(() => {
    if (sinceFromUrl && untilFromUrl) {
      return {
        since: new Date(sinceFromUrl),
        until: new Date(untilFromUrl),
      };
    }
    return {
      since: new Date(new Date().setDate(new Date().getDate() - 30)),
      until: new Date(),
    };
  });

  // Add this new state near your other state declarations
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [criticalDataLoaded, setCriticalDataLoaded] = useState({
    metrics: false,
    insights: false,
    articles: false,
  });

  // ✅ Initialize category from URL params
  const [selectedCategory, setSelectedCategory] = useState(categoryFromUrl);

  // ✅ Initialize article ID from params
  const [selectedArticle, setSelectedArticle] = useState(articleId || "all");

  // Add state for the new Top Articles table
  const [topArticlesData, setTopArticlesData] = useState([]);
  const [topArticlesLoading, setTopArticlesLoading] = useState(false);
  const [topArticlesCount, setTopArticlesCount] = useState(5);
  const [dateRangeArticles, setDateRangeArticles] = useState({
    since: new Date(new Date().setDate(new Date().getDate() - 30)),
    until: new Date(),
  });

  // Add these state variables with your other states
  const [isGeneratingIdeas, setIsGeneratingIdeas] = useState(false);
  const [contentIdeas, setContentIdeas] = useState([]);
  const [showIdeasModal, setShowIdeasModal] = useState(false);
  const [ideasError, setIdeasError] = useState(null);

  // Add this state in your Dashboard component
  const [isExporting, setIsExporting] = useState(false);

  // Add state to store custom date range
  const [customDateRange, setCustomDateRange] = useState(null);

  // Add these new state variables for popover active states
  const [categoryPopoverActive, setCategoryPopoverActive] = useState(false);
  const [articlePopoverActive, setArticlePopoverActive] = useState(false);
  const [topPostsPopoverActive, setTopPostsPopoverActive] = useState(false);
  const [topEngagementPopoverActive, setTopEngagementPopoverActive] =
    useState(false);
  const [topProductsPopoverActive, setTopProductsPopoverActive] =
    useState(false);

  // ============================================
  // ADD THIS: Blog ID to Category Mapping
  // ============================================
  const [blogIdToCategoryMap, setBlogIdToCategoryMap] = useState({});

  // Get article data from navigation state
  const articleData = location.state || {};
  const [articleMetadata, setArticleMetadata] = useState({
    title: "Article Details",
    content: "No Content Available",
    publishedDate: null,
    authorName: "Unknown Author",
    categoryName: "Uncategorized",
  });

  const [articleLoading, setArticleLoading] = useState(true);

  // Add these state variables near your other state declarations
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizedArticle, setOptimizedArticle] = useState(null);
  const [showOptimizedModal, setShowOptimizedModal] = useState(false);
  const [optimizeError, setOptimizeError] = useState(null);

  // Add these state variables with your other states
  const [isGeneratingHeadlines, setIsGeneratingHeadlines] = useState(false);
  const [generatedHeadlines, setGeneratedHeadlines] = useState([]);
  const [showHeadlineModal, setShowHeadlineModal] = useState(false);
  const [headlineError, setHeadlineError] = useState(null);

  // Add this state near your other state declarations
  const [articleInsights, setArticleInsights] = useState({
    article_insights: [],
    recommended_actions: [],
  });
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState(null);

  // Progress States
  const [optimizeProgress, setOptimizeProgress] = useState(0);
  const [headlineProgress, setHeadlineProgress] = useState(0);
  const [ideasProgress, setIdeasProgress] = useState(0);


  // Add these states with your other IdeasModal states
  const [generatingArticleIndex, setGeneratingArticleIndex] = useState(null);
  const [previewArticle, setPreviewArticle] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);


  // Improved helper for smooth progress
  const simulateProgress = (setProgress) => {
    setProgress(0);
    let currentProgress = 0;

    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        // Smaller, consistent increments for smoother animation
        const increment = Math.random() * 2 + 0.5; // 0.5-2.5% increments
        return Math.min(prev + increment, 90);
      });
    }, 100); // Update every 100ms instead of 500ms

    return () => clearInterval(interval);
  };

  const [toastActive, setToastActive] = useState(false);
  const [toastContent, setToastContent] = useState("");
  const [toastError, setToastError] = useState(false);


  // Handler to generate article from idea using OpenAI
  const handleCreateArticle = useCallback(async (idea, ideaIndex) => {
    if (!shopOrigin) {
      console.error('Missing shop origin');
      return;
    }

    setGeneratingArticleIndex(ideaIndex);

    try {
      console.log('Generating article for idea:', idea);

      // Call backend to generate article with OpenAI
      const response = await axios.post(
        '/analytics/generate-article-from-idea/',
        {
          shop: shopOrigin,
          title: idea.title,
          type: idea.type,
          reasoning: idea.reasoning,
          targetMetric: idea.targetMetric || 'engagement',
          // ADD THIS LINE: Pass the current content for rewriting context
          current_content: articleMetadata.content || "", 
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      console.log('Article generation response:', response.data);

      if (response.data.status === 'success') {
        const generatedArticle = {
          title: response.data.article.title,
          content: response.data.article.content,
          ideaType: response.data.article.type,
          reasoning: response.data.article.reasoning,
          descriptive: response.data.article.descriptive,
          targetMetric: response.data.article.targetMetric,
        };

        setPreviewArticle(generatedArticle);
        setShowPreviewModal(true);

        // Show success toast
        setToastContent('Article generated successfully! Review and save as draft.');
        setToastError(false);
        setToastActive(true);

      } else {
        throw new Error(response.data.message || 'Failed to generate article');
      }

    } catch (error) {
      console.error('Error generating article:', error);
      
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Failed to generate article. Please try again.';
      
      console.error('Error details:', {
        message: errorMessage,
        code: error.response?.data?.code,
        details: error.response?.data?.details,
        status: error.response?.status,
      });

      // Show error toast
      setToastContent(errorMessage);
      setToastError(true);
      setToastActive(true);

    } finally {
      setGeneratingArticleIndex(null);
    }
  }, [shopOrigin, articleMetadata.content]);

// Handler to save the generated article as draft
  const handleSaveAsDraft = useCallback(async () => {
    if (!previewArticle || !shopOrigin) {
      console.error('Missing article data or shop origin');
      return;
    }

    setIsSavingDraft(true);

    try {
      // Prepare the payload for publish-article API
      const payload = {
        article_title: previewArticle.title,
        article_content: previewArticle.content,
        status: 'draft', // Save as draft
        blog_id: null, // Will use default blog
        tags: [previewArticle.ideaType, 'AI-generated'],
      };

      console.log('Saving article as draft:', payload);

      // Call your backend API
      const response = await axios.post(
        `/publish-article/${encodeURIComponent(shopOrigin)}/`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      console.log('Save draft response:', response.data);

      if (response.data.status === 'success') {
        const newArticleId = response.data.article_id.split('/').pop();

        
        // Show success toast
        setToastContent(`Article "${previewArticle.title}" saved as draft successfully!`);
        setToastError(false);
        setToastActive(true);

        // Close both modals
        setShowPreviewModal(false);
        setShowIdeasModal(false);

        // Navigate to the newly created article
        setTimeout(() => {
          const adminUrl = `https://${shopOrigin}/admin/articles/${newArticleId}`;
          window.open(adminUrl, "_blank", "noopener,noreferrer");
        }, 1500);

      } else {
        throw new Error(response.data.message || 'Failed to save article');
      }

    } catch (error) {
      console.error('Error saving article as draft:', error);
      
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Failed to save article. Please try again.';
      
      console.error('Error details:', {
        message: errorMessage,
        code: error.response?.data?.code,
        details: error.response?.data?.details,
        status: error.response?.status,
      });

      // Show error toast
      setToastContent(errorMessage);
      setToastError(true);
      setToastActive(true);

    } finally {
      setIsSavingDraft(false);
    }
  }, [previewArticle, shopOrigin, navigate]);


  

  // Update your fetchArticleInsights function
  const fetchArticleInsights = useCallback(async () => {
    if (!shopOrigin || !articleId) return;

    setInsightsLoading(true);
    setInsightsError(null);

    try {
      const params = new URLSearchParams({
        shop: shopOrigin,
        article_id: articleId,
        since: formatDateToYearMonthDayDateString(globalDateRange.since),
        until: formatDateToYearMonthDayDateString(globalDateRange.until),
      });

      // console.log(
      //   "🔄 Fetching article insights with params:",
      //   params.toString()
      // );

      const response = await axios.get(
        `/analytics/generate-article-insights/?${params.toString()}`
      );

      if (response.data.status === "success") {
        setArticleInsights({
          article_insights: response.data.article_insights || [],
          recommended_actions: response.data.recommended_actions || [],
        });

        // Mark insights as loaded
        setCriticalDataLoaded((prev) => ({ ...prev, insights: true }));
        // console.log("✅ Insights loaded successfully");
      } else {
        setInsightsError(response.data.message || "Failed to load insights");
        // Still mark as loaded even on error to not block the UI
        setCriticalDataLoaded((prev) => ({ ...prev, insights: true }));
      }
    } catch (error) {
      console.error("❌ Error fetching article insights:", error);
      setInsightsError("Failed to load insights");
      // Mark as loaded even on error
      setCriticalDataLoaded((prev) => ({ ...prev, insights: true }));
    } finally {
      setInsightsLoading(false);
    }
    // console.log("tru insights");
  }, [shopOrigin, articleId, globalDateRange]);

  // Add this handler function
  const handleGenerateIdeas = useCallback(async () => {
    console.log("Generate Ideas clicked");
    if (!articleId) {
      console.error("Missing article ID");
      return;
    }

    setIsGeneratingIdeas(true);
    setIdeasError(null);

    try {
      // Format dates properly
      const sinceDate = formatDateToYearMonthDayDateString(
        globalDateRange.since
      );
      const untilDate = formatDateToYearMonthDayDateString(
        globalDateRange.until
      );

      //   until: untilDate,      // ✅ Changed from ISO to YYYY-MM-DD format
      // });

      const stopProgress = simulateProgress(setIdeasProgress);


      const response = await axios.post(
        `/analytics/generate-content-ideas/`,
        {
          shop: shopOrigin,
          article_id: articleId, // ✅ Changed from articleid to article_id
          since: sinceDate, // ✅ Changed from ISO to YYYY-MM-DD format
          until: untilDate, // ✅ Changed from ISO to YYYY-MM-DD format
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      stopProgress();
      setIdeasProgress(100);

      if (response.data.status === "success") {
        setContentIdeas(response.data.ideas);
        setShowIdeasModal(true);
        // console.log('✅ Generated ideas:', response.data.ideas);

        // Show success toast
        setToastContent("Content ideas generated successfully!");
        setToastError(false);
        setToastActive(true);
      } else {
        setIdeasError(response.data.message || "Failed to generate ideas");
        setToastContent(response.data.message || "Failed to generate ideas");
        setToastError(true);
        setToastActive(true);
      }
    } catch (error) {
      console.error("❌ Error generating content ideas:", error);

      const errorMessage =
        error.response?.data?.message || "Failed to generate content ideas";
      const errorCode = error.response?.data?.code;

      // console.error('Error details:', {
      //   message: errorMessage,
      //   code: errorCode,
      //   details: error.response?.data?.details,
      //   status: error.response?.status
      // });

      setIdeasError(errorMessage);
      setToastContent(errorMessage);
      setToastError(true);
      setToastActive(true);
    } finally {
      setIsGeneratingIdeas(false);
    }
  }, [articleId, shopOrigin, globalDateRange]);

  // Add useEffect to fetch insights when component mounts or date changes
  useEffect(() => {
    fetchArticleInsights();
  }, [fetchArticleInsights]);

  // ============================================
  // 6. FIXED fetchChartData - Add dependencies
  // ============================================

  // 1. FIRST: Define fetchChartData
  const fetchChartData = useCallback(
    async (daysCount) => {
      setChartLoading(true);
      try {
        const until = new Date();
        const since = new Date();
        since.setDate(until.getDate() - daysCount);

        const params = new URLSearchParams({
          shop: shopOrigin,
          articleid: articleId,
          since: formatDateToYearMonthDayDateString(since),
          until: formatDateToYearMonthDayDateString(until),
        });

        // console.log("Fetching chart data:", params.toString());
        const response = await axios.get(
          `/analytics/article-views-overtime/?${params.toString()}`
        );

        if (Array.isArray(response.data)) {
          return response.data;
        }
        return [];
      } catch (error) {
        console.error("Error fetching chart data:", error);
        return [];
      } finally {
        setChartLoading(false);
      }
    },
    [shopOrigin, articleId]
  );

  // 2. SECOND: Define fetchEngagementChartData - NOW USES NEW ENDPOINT
  const fetchEngagementChartData = useCallback(
    async (daysCount) => {
      setEngagementChartLoading(true);
      try {
        const until = new Date();
        const since = new Date();
        since.setDate(until.getDate() - daysCount);

        const params = new URLSearchParams({
          shop: shopOrigin,
          articleid: articleId,
          since: formatDateToYearMonthDayDateString(since),
          until: formatDateToYearMonthDayDateString(until),
        });

        // console.log('Fetching engagement chart data:', params.toString());

        // NEW: Use dedicated article engagement endpoint
        const response = await axios.get(
          `/analytics/article-engagement-overtime/?${params.toString()}`
        );

        if (Array.isArray(response.data)) {
          return response.data;
        }
        return [];
      } catch (error) {
        console.error("Error fetching engagement chart data:", error);
        return [];
      } finally {
        setEngagementChartLoading(false);
      }
    },
    [shopOrigin, articleId]
  );

  // 3. THIRD: Define handleApplyCustomDateRange
  const handleApplyCustomDateRange = useCallback(
    async (selected) => {
      const period = selected.period || selected;

      // console.log('📅 Custom date range applied:', period);

      setCustomDateRange(period);
      setActiveTab("Custom");

      const params = new URLSearchParams({
        shop: shopOrigin,
        articleid: articleId,
        since: formatDateToYearMonthDayDateString(period.since),
        until: formatDateToYearMonthDayDateString(period.until),
      });

      // Fetch VIEWS data for custom range
      setChartLoading(true);
      try {
        const viewsResponse = await axios.get(
          `/analytics/article-views-overtime/?${params.toString()}`
        );
        // console.log('✅ Custom views data:', viewsResponse.data);

        if (Array.isArray(viewsResponse.data)) {
          setChartData((prevData) => ({
            ...prevData,
            custom: viewsResponse.data,
          }));
        }
      } catch (error) {
        console.error("❌ Error fetching custom views data:", error);
      } finally {
        setChartLoading(false);
      }

      // Fetch ENGAGEMENT data for custom range - UPDATED ENDPOINT
      setEngagementChartLoading(true);
      try {
        const engagementResponse = await axios.get(
          `/analytics/article-engagement-overtime/?${params.toString()}`
        );
        // console.log('✅ Custom engagement data:', engagementResponse.data);

        if (Array.isArray(engagementResponse.data)) {
          setEngagementChartData((prevData) => ({
            ...prevData,
            custom: engagementResponse.data,
          }));
        }
      } catch (error) {
        console.error("❌ Error fetching custom engagement data:", error);
      } finally {
        setEngagementChartLoading(false);
      }
    },
    [shopOrigin, articleId]
  );

  // 4. FINALLY: Put the useEffect AFTER all the above functions
  useEffect(() => {
    if (!shopOrigin || !articleId) return;

    if (activeTab === "Custom") {
      // console.log("⏭️ Skipping useEffect - Custom tab uses Apply button");
      return;
    }

    const loadChartData = async () => {
      const daysMap = {
        "Last 7 days": 7,
        "Last 30 days": 30,
        "Last 90 days": 90,
      };

      const daysCount = daysMap[activeTab] || 30;
      // console.log(`📊 Loading ${activeTab} chart data...`);

      // Fetch VIEWS data
      const viewsData = await fetchChartData(daysCount);
      setChartData((prevData) => ({
        ...prevData,
        [activeTab.toLowerCase()]: viewsData,
      }));

      // Fetch ENGAGEMENT data
      const engagementData = await fetchEngagementChartData(daysCount);
      setEngagementChartData((prevData) => ({
        ...prevData,
        [activeTab.toLowerCase()]: engagementData,
      }));
    };

    loadChartData();
  }, [
    shopOrigin,
    articleId,
    activeTab,
    fetchChartData,
    fetchEngagementChartData,
  ]);

  // Format the published date
  const formatPublishedDate = (dateString) => {
    if (!dateString) return "Date not available";

    const date = new Date(dateString);
    const options = { year: "numeric", month: "short", day: "numeric" };
    return date.toLocaleDateString("en-US", options);
  };

  // const [activeTab, setActiveTab] = useState("Last 30 days");

  // Define ranges for DateRangePicker
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const ranges = [
    {
      title: "Last 7 days",
      alias: "last7days",
      period: {
        since: new Date(
          new Date(new Date().setDate(today.getDate() - 7)).setHours(0, 0, 0, 0)
        ),
        until: today,
      },
    },
    {
      title: "Last 30 days",
      alias: "last30days",
      period: {
        since: new Date(
          new Date(new Date().setDate(today.getDate() - 30)).setHours(
            0,
            0,
            0,
            0
          )
        ),
        until: today,
      },
    },
    {
      title: "Last 90 days",
      alias: "last90days",
      period: {
        since: new Date(
          new Date(new Date().setDate(today.getDate() - 90)).setHours(
            0,
            0,
            0,
            0
          )
        ),
        until: today,
      },
    },
  ];

  // Get initial DateRangePicker config from dateRange
  const getInitialDateRange = useCallback((daysStr) => {
    const days = parseInt(daysStr, 10);
    if (days === 7) return ranges[0];
    if (days === 30) return ranges[1];
    if (days === 90) return ranges[2];
    return ranges[1]; // Default to 30 days
  }, []);

  // Add state for loading and errors
  const [loading, setLoading] = useState(false);
  // const [error, setError] = useState(null);

  const handleOptimizeContent = useCallback(async () => {
    if (!articleMetadata.title || !articleMetadata.content) {
      console.error("Missing article title or content");
      return;
    }

    setIsOptimizing(true);
    setOptimizeError(null);
    const stopProgress = simulateProgress(setOptimizeProgress);

    try {
      const response = await axios.post(
        `/analytics/optimize-article/${encodeURIComponent(shopOrigin)}`,
        {
          title: articleMetadata.title,
          content: articleMetadata.content,
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      stopProgress();
      setOptimizeProgress(100);

      if (response.data.status === "success") {
        const { article } = response.data;
        const optimizedData = {
          title: article.title, // <-- Now matches backend
          content: article.content, // <-- Now matches backend
          descriptive: article.descriptive || "",
          readtime: article.readtime || "",
        };

        // *** FIX: Wrap the article object in an array ***
        setOptimizedArticle([optimizedData]);
        setShowOptimizedModal(true);
      } else {
        setOptimizeError(response.data.message || "Failed to optimize article");
      }
    } catch (error) {
      console.error("Error optimizing article:", error);
      setOptimizeError(
        error.response?.data?.message || "An error occurred. Please try again."
      );
    } finally {
      setIsOptimizing(false);
    }
  }, [articleMetadata, shopOrigin]);

  // Add this handler function
  const handleGenerateHeadlines = useCallback(async () => {
    if (!articleMetadata.title) {
      console.error("Missing article title");
      return;
    }

    setIsGeneratingHeadlines(true);
    setHeadlineError(null);
    const stopProgress = simulateProgress(setHeadlineProgress);

    try {
      const response = await axios.post(
        `/analytics/generate-headlines/${encodeURIComponent(shopOrigin)}`,
        {
          title: articleMetadata.title,
          content: articleMetadata.content || "",
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      stopProgress();
      setHeadlineProgress(100);

      if (response.data.status === "success") {
        setGeneratedHeadlines(response.data.headlines);
        setShowHeadlineModal(true);
      } else {
        setHeadlineError(
          response.data.message || "Failed to generate headlines"
        );
      }
    } catch (error) {
      console.error("Error generating headlines:", error);
      setHeadlineError(
        error.response?.data?.message || "An error occurred. Please try again."
      );
    } finally {
      setIsGeneratingHeadlines(false);
    }
  }, [articleMetadata, shopOrigin]);

  // Add this handler function in ArticleDetail component
  const handleSelectHeadline = useCallback(
    async (selectedHeadline) => {
      setShowHeadlineModal(false);

      // Capture stable values BEFORE updating state
      const originalTitle = articleMetadata.title;
      const originalContent = articleMetadata.content;

      // Optimistically update UI
      setArticleMetadata((prev) => ({
        ...prev,
        title: selectedHeadline,
      }));

      try {
        // BACKEND REQUIRES THIS FIELD — must not be empty
        if (!originalContent || originalContent.trim() === "") {
          throw new Error("Article content is missing");
        }

        const payload = {
          article_id: articleId, // ✔ Fixed name
          article_title: selectedHeadline, // ✔ Fixed name
          article_content: originalContent, // ✔ Fixed name
          status: articleMetadata.publishedDate ? "publish" : "draft",
          blog_id: null,
          tags: [],
        };

        const response = await axios.post(
          `/publish-article/${encodeURIComponent(shopOrigin)}/`,
          payload,
          { headers: { "Content-Type": "application/json" } }
        );

        if (response.data.status === "success") {
          await fetchArticleMetadata();
          setToastContent("Article title updated successfully");
          setToastError(false);
          setToastActive(true);
          // Reload the page after a short delay to show the toast
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } else {
          // Revert title on error
          setArticleMetadata((prev) => ({
            ...prev,
            title: originalTitle,
          }));

          setToastContent(
            response.data.message || "Failed to update article title"
          );
          setToastError(true);
          setToastActive(true);
        }
      } catch (error) {
        // Revert on error
        setArticleMetadata((prev) => ({
          ...prev,
          title: originalTitle,
        }));

        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Error updating article title";

        const errorCode = error.response?.data?.code;

        console.error("Error updating article title:", {
          message: errorMessage,
          code: errorCode,
          details: error.response?.data?.details,
        });

        setToastContent(errorMessage);
        setToastError(true);
        setToastActive(true);
      }
    },
    [
      articleId,
      articleMetadata.content,
      articleMetadata.publishedDate,
      shopOrigin,
    ]
  );

  // ============================================
  // CORRECTED: fetchArticlesAndBlogs useEffect
  // ============================================
  useEffect(() => {
    if (!shopDomain) return;

    const fetchArticlesAndBlogs = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          shop: shopDomain,
        });

        const response = await axios.get(
          `/analytics/get-articles-blogs/?${params.toString()}`
        );
        // console.log("📚 API Response:", response.data);

        if (response.data.status === "success") {
          // Create mapping from blog ID to category name
          const categoryMap = {};
          if (response.data.blogs && Array.isArray(response.data.blogs)) {
            response.data.blogs.forEach((blog) => {
              categoryMap[blog.blog_id] = blog.title;
              // console.log(`Mapped ${blog.blog_id} -> ${blog.title}`);
            });
            // console.log("Final Blog ID to Category Map:", categoryMap);
          }
          setBlogIdToCategoryMap(categoryMap);

          // Format Articles WITH category information
          if (response.data.articles && Array.isArray(response.data.articles)) {
            const formattedArticles = response.data.articles.map((a) => ({
              label: a.article_title || "Untitled",
              value: a.article_id.toString(),
              category: a.category,
              blog_id: a.blog_id,
            }));

            setArticlesList([
              { label: "All Articles", value: "all" },
              ...formattedArticles,
            ]);
            // console.log("✅ Articles loaded:", formattedArticles.length);
          }

          // Format Blogs/Categories
          if (
            response.data.blogs &&
            Array.isArray(response.data.blogs) &&
            response.data.blogs.length > 0
          ) {
            const formattedBlogs = response.data.blogs.map((b) => ({
              label: b.title,
              value: b.blog_id.toString(),
            }));

            setBlogsList([
              { label: "All Categories", value: "all" },
              ...formattedBlogs,
            ]);
            // console.log("✅ Categories loaded:", formattedBlogs.length);
          }

          // Mark articles as loaded
          setCriticalDataLoaded((prev) => ({ ...prev, articles: true }));
          // console.log("✅ Articles/Blogs data loaded successfully");
        } else {
          throw new Error(response.data.message || "An unknown error occurred");
        }
      } catch (err) {
        console.error("❌ Error fetching articles and blogs:", err);
        setError(err.message);
        // Mark as loaded even on error
        setCriticalDataLoaded((prev) => ({ ...prev, articles: true }));
      } finally {
        setLoading(false);
      }
    };

    fetchArticlesAndBlogs();
  }, [shopDomain]);

  // Get shop domain from URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shop = urlParams.get("shop");
    if (shop) {
      setShopDomain(shop);
    }
  }, []);

  // Update fetchDashboardMetrics to mark metrics as loaded
  const fetchDashboardMetrics = useCallback(async () => {
    if (!shopDomain) return;

    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        shop: shopDomain,
        article_id: articleId,
      });

      // Add date range
      if (currentDatePeriod) {
        const sinceDate = formatDateToYearMonthDayDateString(
          currentDatePeriod.since
        );
        const untilDate = formatDateToYearMonthDayDateString(
          currentDatePeriod.until
        );
        params.append("since", sinceDate);
        params.append("until", untilDate);
      } else {
        const sinceDate = formatDateToYearMonthDayDateString(
          globalDateRange.since
        );
        const untilDate = formatDateToYearMonthDayDateString(
          globalDateRange.until
        );
        params.append("since", sinceDate);
        params.append("until", untilDate);
      }

      // Add category filter if available
      if (categoryFromUrl && categoryFromUrl !== "all") {
        params.append("category", categoryFromUrl);
      }

      // console.log(
      //   "📊 Fetching article metrics with params:",
      //   params.toString()
      // );

      const response = await fetch(
        `/analytics/dashboard-metrics/?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      // console.log("✅ Article metrics received:", data);

      // Transform the response data for the 6 metrics
      const transformedData = [
        {
          title: "Total Views",
          value: data.total_blog_views?.value || 0,
          change: data.total_blog_views?.change || null,
          trend: data.total_blog_views?.trend || null,
          subtext: "for this article",
        },
        {
          title: "Clicks to Products",
          value: data.clicks_to_products?.value || 0,
          change: data.clicks_to_products?.change || null,
          trend: data.clicks_to_products?.trend || null,
          subtext: "product clicks",
        },
        {
          title: "Avg Read Time",
          value: data.avg_read_time?.value || "0m 0s",
          change: data.avg_read_time?.change || null,
          trend: data.avg_read_time?.trend || null,
          subtext: "per session",
        },
        {
          title: "Engagement Rate",
          value: data.engagement_rate?.value || 0,
          change: data.engagement_rate?.change || null,
          trend: data.engagement_rate?.trend || null,
          subtext: "% engagement",
        },
        {
          title: "New vs Returning",
          value: data.new_vs_returning?.value || 0,
          change: data.new_vs_returning?.change || null,
          trend: data.new_vs_returning?.trend || null,
          subtext: "% New Visitors",
        },
        {
          title: "Avg Session Duration",
          value: data.avg_session_duration?.value || "0m 0s",
          change: data.avg_session_duration?.change || null,
          trend: data.avg_session_duration?.trend || null,
          subtext: "time spent",
        },
      ];

      setMetricsData(transformedData);

      // Mark metrics as loaded
      setCriticalDataLoaded((prev) => ({ ...prev, metrics: true }));
      // console.log("✅ Metrics loaded successfully");
    } catch (err) {
      console.error("❌ Error fetching article detail metrics:", err);
      setError(err.message || "Failed to load metrics");
      // Mark as loaded even on error
      setCriticalDataLoaded((prev) => ({ ...prev, metrics: true }));
    } finally {
      setIsLoading(false);
    }
  }, [
    shopDomain,
    articleId,
    categoryFromUrl,
    currentDatePeriod,
    globalDateRange,
  ]);

  // Component for rendering trend with color coding
  const TrendCell = ({ value }) => {
    const sign = value > 0 ? "+" : "";
    const formattedValue =
      value % 1 === 0 ? Math.round(value) : value.toFixed(1);
    const tone = value > 0 ? "success" : value < 0 ? "critical" : "subdued";

    return (
      <Text as="span" tone={tone} fontWeight="medium" variant="bodySm">
        {sign}
        {formattedValue}%
      </Text>
    );
  };

  // ============================================
  // 3. FIXED fetchTopPostsBySessions - Add dependencies
  // ============================================
  const fetchTopPostsBySessions = useCallback(
    async (limit) => {
      setSessionLoading(true);
      try {
        const params = new URLSearchParams({
          shop: shopOrigin,
          since: formatDateToYearMonthDayDateString(globalDateRange.since),
          until: formatDateToYearMonthDayDateString(globalDateRange.until),
          limit: limit,
          article_id: articleId, // ✅ Filter by current article
        });

        // Add category filter from URL
        if (categoryFromUrl && categoryFromUrl !== "all") {
          params.append("category", categoryFromUrl);
        }

        const response = await axios.get(
          `/analytics/top-posts/?${params.toString()}`
        );

        const rows = response.data.map((post) => [
          <Link
            className="Polaris-Text--root Polaris-Text--bodySm"
            to={`/article/${post.article_id}?shop=${encodeURIComponent(
              shopOrigin || ""
            )}`}
            style={{
              color: "#005BD3",
              textDecoration: "none",
              cursor: "pointer",
            }}
          >
            {post.title || "Untitled"}
          </Link>,
          <InlineStack gap="150" blockAlign="center" align="end">
            <ToolTip content="Total number of views for this article">
              <Text as="span">{post.views.toString()}</Text>
            </ToolTip>
          </InlineStack>,
          <TrendCell value={post.trend} />,
        ]);
        setTopPostsBySessions(rows);
      } catch (error) {
        console.error("Error fetching top posts by sessions:", error);
        setTopPostsBySessions([]);
      } finally {
        setSessionLoading(false);
      }
    },
    [shopOrigin, selectedArticle, selectedCategory, globalDateRange]
  );

  // ============================================
  // 4. FIXED fetchTopEngagement - Add dependencies
  // ============================================
  const fetchTopEngagement = useCallback(
    async (limit) => {
      setEngagementLoading(true);
      try {
        const params = new URLSearchParams({
          shop: shopOrigin,
          since: formatDateToYearMonthDayDateString(globalDateRange.since),
          until: formatDateToYearMonthDayDateString(globalDateRange.until),
          limit: limit,
        });

        // ✅ CRITICAL: Add these filters
        if (selectedArticle && selectedArticle !== "all") {
          params.append("article_id", selectedArticle);
          // console.log("Engagement filtering by article:", selectedArticle);
        }
        // ✅ FIXED: Convert blog ID to category name
        if (selectedCategory && selectedCategory !== "all") {
          const categoryName = blogIdToCategoryMap[selectedCategory];
          if (categoryName) {
            params.append("category", categoryName);
            // console.log(
            //   "✅ Engagement - Filtering by category name:",
            //   categoryName
            // );
          }
        }

        const response = await axios.get(
          `/analytics/engagement/?${params.toString()}`
        );

        const rows = response.data.per_article.map((article) => [
          <Link
            className="Polaris-Text--root Polaris-Text--bodySm"
            to={`/article/${article.article_id}?shop=${encodeURIComponent(
              shopOrigin || ""
            )}`}
            style={{
              color: "#005BD3",
              textDecoration: "none",
              cursor: "pointer",
            }}
          >
            {article.title || "Untitled"}
          </Link>,
          <InlineStack gap="150" blockAlign="center" align="end">
            <Text as="span">{article.engagement_percentage.toFixed(1)}%</Text>
          </InlineStack>,
          <TrendCell value={article.trend} />,
        ]);
        setTopEngagementData(rows);
      } catch (error) {
        console.error("Error fetching engagement data:", error);
        setTopEngagementData([]);
      } finally {
        setEngagementLoading(false);
      }
    },
    [shopOrigin, selectedArticle, selectedCategory, globalDateRange]
  );

  // ============================================
  // 5. FIXED fetchTopProducts - Add dependencies
  // ============================================
  const fetchTopProducts = useCallback(
    async (limit) => {
      setTopProductsLoading(true);
      try {
        const params = new URLSearchParams({
          shop: shopOrigin,
          since: formatDateToYearMonthDayDateString(globalDateRange.since),
          until: formatDateToYearMonthDayDateString(globalDateRange.until),
          limit: limit,
        });

        if (selectedArticle && selectedArticle !== "all") {
          params.append("article_id", selectedArticle); // Use 'article_id' to match backend
        }

        if (selectedCategory && selectedCategory !== "all") {
          const categoryName = blogIdToCategoryMap[selectedCategory];
          if (categoryName) {
            params.append("category", categoryName);
          }
        }

        // console.log("🔍 Fetching top products with params:", params.toString());

        // FIX: Use the correct endpoint path
        const response = await axios.get(
          `/analytics/top-clicked-products/?${params.toString()}`
        );

        // console.log("📦 Top products response:", response.data);

        if (Array.isArray(response.data)) {
          setTopProductsData(response.data);
        } else {
          console.error("❌ API returned invalid data format:", response.data);
          setTopProductsData([]);
        }
      } catch (error) {
        console.error("❌ Error fetching top products:", error);
        setTopProductsData([]);
      } finally {
        setTopProductsLoading(false);
      }
    },
    [
      shopOrigin,
      selectedArticle,
      selectedCategory,
      globalDateRange,
      blogIdToCategoryMap,
    ]
  );

  // Fetch article metadata on mount
  // Move the fetch logic into a reusable function
  const fetchArticleMetadata = useCallback(async () => {
    if (!shopOrigin || !articleId) return;

    setArticleLoading(true);
    try {
      // Extract numeric article ID from the format "article_id=610390540587"
      const numericArticleId = articleId.split("=")[1] || articleId;

      const params = new URLSearchParams({
        shop: shopOrigin,
        articleid: `gid://shopify/Article/${numericArticleId}`, // Format as GraphQL ID
      });

      const response = await axios.get(
        `/analytics/get-articles-blogs/?${params.toString()}`
      );

      if (response.data.status === "success" && response.data.article) {
        const article = response.data.article;
        // console.log("Article metadata response: ", article);

        setArticleMetadata({
          title: article.articletitle || "Untitled Article",
          content: article.articlecontent || "No Content Available",
          publishedDate: article.publishedat,
          authorName: article.author || "Unknown Author",
          categoryName: article.blogtitle || "Uncategorized",
        });
      }
    } catch (error) {
      console.error("Error fetching article metadata:", error);
    } finally {
      setArticleLoading(false);
    }
  }, [shopOrigin, articleId]);

  // Call it inside a useEffect
  useEffect(() => {
    fetchArticleMetadata();
  }, [fetchArticleMetadata]);

  // ============================================
  // 7. FIXED fetchTopArticles - Add dependencies
  // ============================================
  const fetchTopArticles = useCallback(
    async (limit) => {
      setTopArticlesLoading(true);
      try {
        const params = new URLSearchParams({
          shop: shopOrigin,
          since: formatDateToYearMonthDayDateString(globalDateRange.since),
          until: formatDateToYearMonthDayDateString(globalDateRange.until),
          limit: limit,
        });

        // ✅ CRITICAL: Add these filters
        if (selectedArticle && selectedArticle !== "all") {
          params.append("article_id", selectedArticle);
          // console.log("Top articles filtering by article:", selectedArticle);
        }
        // ✅ FIXED: Convert blog ID to category name
        if (selectedCategory && selectedCategory !== "all") {
          const categoryName = blogIdToCategoryMap[selectedCategory];
          if (categoryName) {
            params.append("category", categoryName);
          }
        }

        const response = await axios.get(
          `/analytics/top-articles-data/?${params.toString()}`
        );

        const rows = response.data.map((article) => [
          <Link
            className="Polaris-Text--root Polaris-Text--bodySm"
            to={`/article/${article.article_id}?shop=${encodeURIComponent(
              shopOrigin || ""
            )}`}
            style={{
              color: "#005BD3",
              textDecoration: "none",
              cursor: "pointer",
            }}
          >
            {article.title || "Untitled Article"}
          </Link>,
          article.views,
          article.conversions,
        ]);
        setTopArticlesData(rows);
      } catch (error) {
        console.error("Error fetching top articles data:", error);
        setTopArticlesData([]);
      } finally {
        setTopArticlesLoading(false);
      }
    },
    [shopOrigin, selectedArticle, selectedCategory, globalDateRange]
  );

  // ============================================
  // 8. FIXED useEffect for topSessions
  // ============================================
  useEffect(() => {
    if (!shopOrigin) return;
    // console.log(
    //   "Refetching topSessions - selectedArticle:",
    //   selectedArticle,
    //   "selectedCategory:",
    //   selectedCategory
    // );
    fetchTopPostsBySessions(topPostsSessionsCount);
  }, [
    shopOrigin,
    globalDateRange,
    fetchTopPostsBySessions,
    topPostsSessionsCount,
    selectedArticle, // ✅ CRITICAL: Add this
    selectedCategory, // ✅ CRITICAL: Add this
    // blogIdToCategoryMap, // Add this
  ]);

  // ============================================
  // 9. FIXED useEffect for topEngagement
  // ============================================
  useEffect(() => {
    if (!shopOrigin) return;
    // console.log(
    //   "Refetching topEngagement - selectedArticle:",
    //   selectedArticle,
    //   "selectedCategory:",
    //   selectedCategory
    // );
    fetchTopEngagement(topEngagementCount);
  }, [
    shopOrigin,
    globalDateRange,
    fetchTopEngagement,
    topEngagementCount,
    selectedArticle, // ✅ CRITICAL: Add this
    selectedCategory, // ✅ CRITICAL: Add this
    // blogIdToCategoryMap, // Add this
  ]);

  // ============================================
  // 10. FIXED useEffect for topProducts
  // ============================================
  useEffect(() => {
    if (!shopOrigin) return;
    // console.log(
    //   "Refetching topProducts - selectedArticle:",
    //   selectedArticle,
    //   "selectedCategory:",
    //   selectedCategory
    // );
    fetchTopProducts(topProductsCount);
  }, [
    shopOrigin,
    globalDateRange,
    fetchTopProducts,
    topProductsCount,
    selectedArticle, // ✅ CRITICAL: Add this
    selectedCategory, // ✅ CRITICAL: Add this
    // blogIdToCategoryMap, // Add this
  ]);

  // ============================================
  // 12. FIXED useEffect for topArticles
  // ============================================
  useEffect(() => {
    if (!shopOrigin) return;
    // console.log(
    //   "Refetching topArticles - selectedArticle:",
    //   selectedArticle,
    //   "selectedCategory:",
    //   selectedCategory
    // );
    fetchTopArticles(topArticlesCount);
  }, [
    shopOrigin,
    globalDateRange,
    topArticlesCount,
    fetchTopArticles,
    selectedArticle, // ✅ CRITICAL: Add this
    selectedCategory, // ✅ CRITICAL: Add this
    // blogIdToCategoryMap, // Add this
  ]);

  // Fetch dashboard metrics
  useEffect(() => {
    fetchDashboardMetrics();
  }, [fetchDashboardMetrics]);

  // ADD THIS: Auto-select category when article is selected
  // Corrected: do NOT force category to 'all' when no article is selected
  useEffect(() => {
    // If no article or “All Articles” is selected, leave category as-is
    if (!selectedArticle || selectedArticle === "all") {
      return;
    }

    // Find the selected article
    const selectedArticleObj = articlesList.find(
      (a) => a.value === selectedArticle
    );
    if (!selectedArticleObj || !selectedArticleObj.category) return;

    // Map article.category → blogId and set category
    let matchingBlogId = "all";
    for (const [blogId, categoryName] of Object.entries(blogIdToCategoryMap)) {
      if (
        categoryName.toLowerCase() === selectedArticleObj.category.toLowerCase()
      ) {
        matchingBlogId = blogId;
        break;
      }
    }
    setSelectedCategory(matchingBlogId);
  }, [selectedArticle, articlesList, blogIdToCategoryMap]);

  const defaultGridLayout = [
    { id: "totalBlogViews", colSpan: 4 },
    { id: "clicksToProducts", colSpan: 4 },
    { id: "avgReadTime", colSpan: 4 },
    { id: "engagementRate", colSpan: 4 },
    { id: "newVSreturning", colSpan: 4 },
    { id: "avgSessionDuration", colSpan: 4 },
    { id: "articleInsights", colSpan: 6 },
    { id: "recommendedActions", colSpan: 6 },
    { id: "chartovertime", colSpan: 12 },
    { id: "aiContentOptimisation", colSpan: 12 },
    { id: "topArticles", colSpan: 12 },
  ];

  const fetchInsights = useCallback(async () => {
    if (!shopOrigin) return;

    setInsightsLoading(true);
    try {
      const response = await axios.get(
        `/analytics/generate-insights/?shop=${shopOrigin}&days=30`
      );

      // Ensure the response has the expected structure
      setInsights({
        performance_insights: response.data.performance_insights || [],
        content_optimization: response.data.content_optimization || [],
        strategic_actions: response.data.strategic_actions || [],
      });
    } catch (error) {
      console.error("Error fetching insights:", error);
      setInsights({
        performance_insights: [],
        content_optimization: [],
        strategic_actions: [],
      });
    } finally {
      setInsightsLoading(false);
    }
  }, [shopOrigin]);

  useEffect(() => {
    if (!shopOrigin) return;
    fetchInsights();
  }, [shopOrigin]);

  const calculateRowLayout = (items) => {
    const GRID_COLUMNS = 12;
    const rowLayouts = [];
    let currentRow = [];
    let currentRowSpan = 0;

    items.forEach((item, index) => {
      const nextSpan = currentRowSpan + item.colSpan;

      if (nextSpan <= GRID_COLUMNS) {
        currentRow.push({ ...item, adjustedColSpan: item.colSpan });
        currentRowSpan = nextSpan;
      } else {
        const remainingSpace = GRID_COLUMNS - currentRowSpan;

        if (remainingSpace >= 4 && item.colSpan > remainingSpace) {
          currentRow.push({ ...item, adjustedColSpan: remainingSpace });
          rowLayouts.push(currentRow);
          currentRow = [];
          currentRowSpan = 0;
        } else {
          if (currentRow.length > 0 && remainingSpace > 0) {
            const lastItem = currentRow[currentRow.length - 1];
            lastItem.adjustedColSpan =
              (lastItem.adjustedColSpan || lastItem.colSpan) + remainingSpace;
          }

          rowLayouts.push(currentRow);
          currentRow = [{ ...item, adjustedColSpan: item.colSpan }];
          currentRowSpan = item.colSpan;
        }
      }
      if (index === items.length - 1 && currentRow.length > 0) {
        const remainingSpace = GRID_COLUMNS - currentRowSpan;
        if (remainingSpace > 0 && currentRow.length > 0) {
          const lastItem = currentRow[currentRow.length - 1];
          lastItem.adjustedColSpan =
            (lastItem.adjustedColSpan || lastItem.colSpan) + remainingSpace;
        }
        rowLayouts.push(currentRow);
      }
    });
    return rowLayouts.flat();
  };

  const customCollisionDetection = (args) => {
    const closestCornersCollisions = closestCorners(args);
    if (closestCornersCollisions.length > 0) {
      return closestCornersCollisions;
    }
    return closestCenter(args);
  };

  // Add API functions
  const loadLayoutFromDB = async (shopDomain) => {
    try {
      const response = await axios.get(
        `/analytics/get-layout-article?shop=${shopDomain}`
      );
      if (
        response.data.status === "success" &&
        response.data.layout.length > 0
      ) {
        return response.data.layout;
      }
      return defaultGridLayout;
    } catch (error) {
      console.error("Error loading layout from DB:", error);
      return defaultGridLayout;
    }
  };

  const saveLayoutToDB = async (shopDomain, layout) => {
    try {
      const response = await axios.post(
        `/analytics/save-layout-article?shop=${shopDomain}`,
        { layout },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data.status === "success";
    } catch (error) {
      console.error("Error saving layout to DB:", error);
      return false;
    }
  };

  const resetLayoutInDB = async (shopDomain) => {
    try {
      const response = await axios.post(
        `/analytics/reset-layout-article?shop=${shopDomain}`
      );
      return response.data.status === "success";
    } catch (error) {
      console.error("Error resetting layout in DB:", error);
      return false;
    }
  };

  // Update state initialization
  const [editMode, setEditMode] = useState(false);
  const [draggingHeight, setDraggingHeight] = useState({});
  const [gridLayout, setGridLayout] = useState([]);
  const [layoutLoading, setLayoutLoading] = useState(true);
  const [isSavingLayout, setIsSavingLayout] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  // Load layout from DB on mount
  useEffect(() => {
    if (!shopDomain) return;

    const loadLayout = async () => {
      setLayoutLoading(true);
      try {
        const layout = await loadLayoutFromDB(shopDomain);
        setGridLayout(calculateRowLayout(layout));
      } catch (error) {
        console.error("Failed to load layout:", error);
        setGridLayout(calculateRowLayout(defaultGridLayout));
      } finally {
        setLayoutLoading(false);
      }
    };

    loadLayout();
  }, [shopDomain]);

  // Auto-save layout when it changes (debounced)
  useEffect(() => {
    if (!shopDomain || layoutLoading || gridLayout.length === 0) return;

    // Debounce save to avoid too many API calls
    const timeoutId = setTimeout(async () => {
      setIsSavingLayout(true);
      await saveLayoutToDB(shopDomain, gridLayout);
      setIsSavingLayout(false);
    }, 1000); // Save 1 second after last change

    return () => clearTimeout(timeoutId);
  }, [gridLayout, shopDomain, layoutLoading]);

  // Update drag end handler
  function handleDragEnd({ active, over }) {
    if (!over || active.id === over.id) return;

    setGridLayout((items) => {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newItems = [...items];
        newItems[oldIndex].colSpan = newItems[newIndex].colSpan;
        const reordered = arrayMove(newItems, oldIndex, newIndex);

        const preservedSpans = reordered.map((item) => ({
          ...item,
          colSpan: item.colSpan || item.adjustedColSpan || 4,
        }));

        return calculateRowLayout(preservedSpans);
      }
      return items;
    });

    setDraggingHeight((prev) => {
      const newPrev = { ...prev };
      delete newPrev[active.id];
      return newPrev;
    });
  }

  // Update reset handler
  const handleResetLayout = async () => {
    if (!shopDomain) return;

    setLayoutLoading(true);
    const success = await resetLayoutInDB(shopDomain);

    if (success) {
      setGridLayout(calculateRowLayout(defaultGridLayout));
    }

    setLayoutLoading(false);
    setEditMode(false);
  };

  // Add this function before renderCard
  const createHeaderWithTooltip_top5article = (label, tooltipContent) => {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "4px",
          marginRight: "-6px",
        }}
      >
        <span style={{ fontWeight: 600, fontSize: "13px" }}>{label}</span>
        <ToolTip content={tooltipContent} dismissOnMouseOut>
          <Icon source={InfoIcon} tone="base" />
        </ToolTip>
      </div>
    );
  };

  // --- UPDATE DATE RANGE HANDLER ---
  const handleDateRangeChange = useCallback((selected) => {
    // ✅ Handle both cases: full object or just period
    const period = selected.period || selected;

    // Calculate days from the period
    const calculateDays = (period) => {
      const diffTime = Math.abs(period.until - period.since);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    };

    const calculatedDays = calculateDays(period);

    setDateRange(calculatedDays);
    setCurrentDatePeriod(period);
    setGlobalDateRange({
      since: period.since,
      until: period.until,
    });
  }, []);

  // New useEffect to check if all critical data is loaded
  useEffect(() => {
    const allLoaded =
      criticalDataLoaded.metrics &&
      criticalDataLoaded.insights &&
      criticalDataLoaded.articles;

    if (allLoaded && isInitialLoading) {
      // console.log("✅ All critical data loaded, showing dashboard");
      setIsInitialLoading(false);
    }
  }, [criticalDataLoaded, isInitialLoading]);

  const safeChartData = chartData[activeTab.toLowerCase()];
  const currentChartData = Array.isArray(safeChartData) ? safeChartData : [];

  const formatDateLabel = (dateString) => {
    try {
      const date = new Date(dateString);
      const options = { month: "short", day: "numeric" };
      return date.toLocaleDateString("en-US", options);
    } catch (error) {
      console.error("Error formatting date", dateString, error);
      return dateString;
    }
  };

  const chartCategories =
    currentChartData.length > 0
      ? currentChartData.map((item) => formatDateLabel(item.date))
      : [];

  const chartSeriesData =
    currentChartData.length > 0
      ? currentChartData.map((item) => item.value || 0)
      : [];

  // Calculate marker interval based on screen size
  const getMarkerInterval = () => {
    const dataLength = chartCategories.length;
    if (smDown) {
      if (dataLength > 30) return 5;
      if (dataLength > 14) return 3;
      return 1;
    }
    return 1;
  };

  // Create discrete markers array
  const markerInterval = getMarkerInterval();
  const discreteMarkers = [];
  currentChartData.forEach((item, index) => {
    if (index % markerInterval === 0) {
      discreteMarkers.push({
        seriesIndex: 0,
        dataPointIndex: index,
        fillColor: "#6366F1",
        strokeColor: "#fff",
        size: 5,
      });
    }
  });

  // Dynamic tick amount based on screen size
  const getTickAmount = () => {
    const dataLength = chartCategories.length;
    if (smDown) {
      if (dataLength > 30) return 6;
      if (dataLength > 14) return 4;
      return Math.min(dataLength, 5);
    } else if (mdDown) {
      if (dataLength > 30) return 10;
      return Math.min(dataLength, 10);
    }
    return dataLength > 30 ? 15 : dataLength;
  };

  const chartOptions = useMemo(
    () => ({
      chart: {
        type: "line",
        height: 350,
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      stroke: {
        curve: "smooth",
        width: 2.5,
        colors: ["#6366F1"],
      },
      markers: {
        size: 0,
        discrete: discreteMarkers,
        hover: {
          size: 6,
          sizeOffset: 2,
        },
      },
      xaxis: {
        categories: chartCategories,
        tickAmount: getTickAmount(),
        labels: {
          style: {
            colors: "#6B7280",
            fontSize: smDown ? "9px" : "11px",
            fontWeight: 500,
          },
          rotate: -45,
          rotateAlways: false,
          hideOverlappingLabels: true,
          trim: false,
          maxHeight: 120,
        },
        axisBorder: {
          show: true,
          color: "#E5E7EB",
        },
        axisTicks: {
          show: true,
          color: "#E5E7EB",
        },
      },
      yaxis: {
        title: {
          text: "Views",
          style: {
            color: "#6B7280",
            fontSize: "13px",
            fontWeight: 500,
          },
        },
        labels: {
          style: {
            colors: "#6B7280",
            fontSize: "11px",
            fontWeight: 500,
          },
        },
        axisBorder: {
          show: true,
          color: "#E5E7EB",
        },
      },
      grid: {
        borderColor: "#F3F4F6",
        strokeDashArray: 0,
        xaxis: {
          lines: {
            show: false,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
      tooltip: {
        enabled: true,
        y: {
          formatter: (val) => val.toLocaleString(),
        },
      },
    }),
    [chartCategories, activeTab, smDown, discreteMarkers]
  ); // ✅ Add dependencies

  const handleExportData = async () => {
    try {
      const params = new URLSearchParams({
        shop: shopOrigin,
        since: formatDateToYearMonthDayDateString(globalDateRange.since),
        until: formatDateToYearMonthDayDateString(globalDateRange.until),
      });

      if (selectedArticle && selectedArticle !== "all") {
        params.append("article_id", selectedArticle);
      }

      if (selectedCategory && selectedCategory !== "all") {
        params.append("category", blogIdToCategoryMap[selectedCategory]);
      }

      const response = await axios.get(
        `/analytics/export-article-data/?${params.toString()}`,
        { responseType: "blob" }
      );

      // Download file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `article_analytics_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();

      // console.log('✅ Excel file downloaded successfully');
    } catch (error) {
      console.error("❌ Error exporting data:", error);
      alert("Failed to export data");
    }
  };

  const renderCard = (cardId) => {
    const renderMetricCard = (metric, index) => {
      if (!metric) {
        return (
          <Card>
            <BlockStack gap="300">
              <Text as="p" variant="bodySm" tone="subdued">
                Loading...
              </Text>
            </BlockStack>
          </Card>
        );
      }

      // Define tooltip content for each card
      const tooltipContent = {
        "Total Views": "Total number of views across all blog articles.",
        "Clicks to Products":
          "Total product clicks generated directly from blog articles.",
        "Avg Read Time": "Average time readers spend on the article.",
        "Engagement Rate":
          "Percentage of users interacting meaningfully with your article.",
        "New vs Returning":
          "Shows the percentage split between new and returning visitors.",
        "Avg Session Duration":
          "Average time visitors spend during a session on your blog.",
      };

      return (
        <Card>
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
              <div
                style={{ display: "flex", alignItems: "center", gap: "2px" }}
              >
                <Text as="p" variant="bodySm" tone="subdued">
                  {metric.title}
                </Text>
                <ToolTip
                  content={tooltipContent[metric.title]}
                  dismissOnMouseOut
                >
                  <Icon source={InfoIcon} tone="base" />
                </ToolTip>
              </div>
              <div>
                <span
                  style={{
                    display: "inline-block",
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: metric.dotColor,
                  }}
                />
              </div>
            </InlineStack>

            {/* Dynamic variant based on card index */}
            <Text
              as="h2"
              variant={index === 2 ? "heading3xl" : "heading3xl"}
              fontWeight="bold"
            >
              {metric.value}
            </Text>

            {/* Subvalue for "Top Product Clicks" card */}
            {metric.subvalue && (
              <Text variant="headingXl" as="h3" fontWeight="semibold">
                {metric.subvalue}
              </Text>
            )}

            <InlineStack gap="100" blockAlign="center">
              {metric.change && (
                <>
                  <Text
                    as="span"
                    tone={
                      metric.trend === "up"
                        ? "success"
                        : metric.trend === "down"
                        ? "critical"
                        : "subdued"
                    }
                    fontWeight="medium"
                  >
                    {metric.change}%{" "}
                    {metric.trend === "up"
                      ? "↑"
                      : metric.trend === "down"
                      ? "↓"
                      : ""}
                  </Text>
                  <Text as="span" variant="bodySm" tone="subdued">
                    {metric.subtext}
                  </Text>
                </>
              )}
              {!metric.change && metric.subtext && (
                <Text as="span" variant="bodySm" tone="subdued">
                  {metric.subtext}
                </Text>
              )}
            </InlineStack>
          </BlockStack>
        </Card>
      );
    };

    switch (cardId) {
      case "totalBlogViews":
        return renderMetricCard(metricsData?.[0], 0);
      case "clicksToProducts":
        return renderMetricCard(metricsData?.[1], 1);
      case "avgReadTime":
        return renderMetricCard(metricsData?.[2], 2);
      case "engagementRate":
        return renderMetricCard(metricsData?.[3], 3);
      case "newVSreturning":
        return renderMetricCard(metricsData?.[4], 4);
      case "avgSessionDuration":
        return renderMetricCard(metricsData?.[5], 5);

      case "articleInsights":
        return (
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h3" variant="headingMd">
                  📄 Article Insights
                </Text>
                {insightsLoading && <Spinner size="small" />}
              </InlineStack>

              <BlockStack gap="400">
                {insightsLoading ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      padding: "40px",
                    }}
                  >
                    <Spinner size="large" />
                  </div>
                ) : insightsError ? (
                  <Text as="p" variant="bodySm" tone="subdued">
                    {insightsError}
                  </Text>
                ) : articleInsights.article_insights &&
                  articleInsights.article_insights.length > 0 ? (
                  articleInsights.article_insights.map((insight, index) => (
                    <Card
                      key={insight.id || `insight-${index}`}
                      background="bg-surface-secondary"
                    >
                      <Text as="p" variant="bodySm">
                        {insight.parts && Array.isArray(insight.parts)
                          ? insight.parts.map((part, pIndex) =>
                              typeof part === "string" ? (
                                part
                              ) : part.type === "link" ? (
                                <Link
                                  key={pIndex}
                                  to={`/article/${
                                    insight.article_id
                                  }?shop=${encodeURIComponent(shopOrigin)}`}
                                  style={{
                                    color: "#005BD3",
                                    textDecoration: "none",
                                  }}
                                >
                                  {part.text}
                                </Link>
                              ) : (
                                part.text || JSON.stringify(part)
                              )
                            )
                          : JSON.stringify(insight)}
                      </Text>
                    </Card>
                  ))
                ) : (
                  <Text as="p" variant="bodySm" tone="subdued">
                    No insights available yet. Keep creating content!
                  </Text>
                )}
              </BlockStack>
            </BlockStack>
          </Card>
        );

      case "recommendedActions":
        return (
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h3" variant="headingMd">
                  ✅ Recommended Actions
                </Text>
                {insightsLoading && <Spinner size="small" />}
              </InlineStack>

              <BlockStack gap="400">
                {insightsLoading ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      padding: "40px",
                    }}
                  >
                    <Spinner size="large" />
                  </div>
                ) : insightsError ? (
                  <Text as="p" variant="bodySm" tone="subdued">
                    {insightsError}
                  </Text>
                ) : articleInsights.recommended_actions &&
                  articleInsights.recommended_actions.length > 0 ? (
                  articleInsights.recommended_actions.map((action, index) => (
                    <Card
                      key={action.id || `action-${index}`}
                      background="bg-surface-secondary"
                    >
                      <Text as="p" variant="bodySm">
                        {action.parts && Array.isArray(action.parts)
                          ? action.parts.map((part, pIndex) =>
                              typeof part === "string" ? (
                                part
                              ) : part.type === "link" ? (
                                <Link
                                  key={pIndex}
                                  to={`/article/${
                                    action.article_id
                                  }?shop=${encodeURIComponent(shopOrigin)}`}
                                  style={{
                                    color: "#005BD3",
                                    textDecoration: "none",
                                  }}
                                >
                                  {part.text}
                                </Link>
                              ) : (
                                part.text || JSON.stringify(part)
                              )
                            )
                          : JSON.stringify(action)}
                      </Text>
                    </Card>
                  ))
                ) : (
                  <Text as="p" variant="bodySm" tone="subdued">
                    No recommendations available yet. Keep creating content!
                  </Text>
                )}
              </BlockStack>
            </BlockStack>
          </Card>
        );

      case "chartovertime":
        // Determine which dataset to use based on active metric
        const activeChartData =
          chartActiveMetric === "views"
            ? chartData[activeTab.toLowerCase()] || []
            : engagementChartData[activeTab.toLowerCase()] || [];

        const activeLoading =
          chartActiveMetric === "views" ? chartLoading : engagementChartLoading;

        // Format data for chart
        const currentData = Array.isArray(activeChartData)
          ? activeChartData
          : [];
        const categories =
          currentData.length > 0
            ? currentData.map((item) => formatDateLabel(item.date))
            : [];
        const seriesData =
          currentData.length > 0
            ? currentData.map((item) => item.value || 0)
            : [];

        // Calculate markers
        const markerInterval = getMarkerInterval();
        const discreteMarkers = [];
        currentData.forEach((item, index) => {
          if (index % markerInterval === 0) {
            discreteMarkers.push({
              seriesIndex: 0,
              dataPointIndex: index,
              fillColor: chartActiveMetric === "views" ? "#6366F1" : "#10B981",
              strokeColor: "#fff",
              size: 5,
            });
          }
        });

        // Chart options with dynamic color
        const dynamicChartOptions = {
          ...chartOptions,
          stroke: {
            ...chartOptions.stroke,
            colors: [chartActiveMetric === "views" ? "#6366F1" : "#10B981"],
          },
          markers: {
            ...chartOptions.markers,
            discrete: discreteMarkers,
          },
          xaxis: {
            ...chartOptions.xaxis,
            categories: categories,
          },
          yaxis: {
            ...chartOptions.yaxis,
            title: {
              text: chartActiveMetric === "views" ? "Views" : "Engagement %",
              style: {
                color: "#6B7280",
                fontSize: "13px",
                fontWeight: 500,
              },
            },
          },
          tooltip: {
            enabled: true,
            y: {
              formatter: (val) =>
                chartActiveMetric === "views"
                  ? val.toLocaleString()
                  : val.toFixed(1) + "%",
            },
          },
        };

        const dynamicChartSeries = [
          {
            name: chartActiveMetric === "views" ? "Views" : "Engagement Rate",
            data: seriesData,
          },
        ];

        return (
          <Card>
            <BlockStack gap="300">
              {/* Header with Metric Tabs */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "12px",
                }}
              >
                <Text as="h3" variant="headingMd">
                  {chartActiveMetric === "views"
                    ? "Views Over Time"
                    : "Engagement Over Time"}
                </Text>

                {/* Metric Toggle: Views vs Engagement */}
                <div style={{ marginLeft: "auto" }}>
                  <DateRangePicker
                    initialDateRange={getInitialDateRange(dateRange)}
                    onChange={handleApplyCustomDateRange}
                    disabled={isLoading}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "4px",
                    backgroundColor: "#F3F4F6",
                    padding: "2px",
                    borderRadius: "10px",
                  }}
                >
                  {["views", "engagement"].map((metric) => (
                    <button
                      key={metric}
                      onClick={() => setChartActiveMetric(metric)}
                      style={{
                        background:
                          chartActiveMetric === metric ? "#fff" : "transparent",
                        color:
                          chartActiveMetric === metric ? "#111928" : "#6B7280",
                        border:
                          chartActiveMetric === metric
                            ? "1.5px solid #E5E7EB"
                            : "none",
                        borderRadius: "8px",
                        fontWeight: 500,
                        padding: "6px 15px",
                        boxShadow:
                          chartActiveMetric === metric
                            ? "0 1px 2px rgba(16, 24, 40, 0.05)"
                            : "none",
                        cursor: "pointer",
                        transition: "background 0.2s, color 0.2s",
                        textTransform: "capitalize",
                      }}
                    >
                      {metric}
                    </button>
                  ))}
                </div>
              </div>

              {/* Chart Content */}
              {activeLoading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "40px",
                  }}
                >
                  <Spinner size="large" />
                </div>
              ) : currentData.length > 0 ? (
                <div
                  style={{
                    width: "100%",
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    padding: "16px",
                  }}
                >
                  <Chart
                    key={`chart-${chartActiveMetric}-${activeTab}-${currentData.length}`}
                    options={dynamicChartOptions}
                    series={dynamicChartSeries}
                    type="line"
                    height={350}
                  />
                </div>
              ) : (
                <Text as="p" tone="subdued">
                  No {chartActiveMetric} data available for the selected period.
                </Text>
              )}
            </BlockStack>
          </Card>
        );

      case "aiContentOptimisation":
        return (
          <Card background="bg-surface-info">
            <BlockStack gap="400">
              {/* Header Section */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  paddingBottom: "12px",
                }}
              >
                <div>
                  <Text as="h3" variant="headingMd" fontWeight="semibold">
                    🤖 AI Content Optimization
                  </Text>
                </div>
                <div>
                  <Badge tone="info">
                    <Link
                      to="https://apps.shopify.com/bloghero"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "inherit",
                        textDecoration: "none",
                        cursor: "pointer",
                      }}
                    >
                      <span>Powered by BlogHero</span>
                    </Link>
                  </Badge>
                </div>
              </div>

              {/* Improve Article Text Section */}
              <Card background="bg-surface-secondary">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "16px",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <BlockStack gap="100">
                      <Text as="h4" variant="headingSm" fontWeight="semibold">
                        Improve Article Text
                      </Text>
                      <Text as="p" variant="bodySm" tone="subdued">
                        Optimize content for readability, SEO, and conversions
                      </Text>
                    </BlockStack>
                  </div>
                  <div>
                    {/* <Button
                      background="bg-fill-emphasis"
                      // variant="primary"
                      icon={<Icon source={WandIcon} />}
                    >
                      Optimize Content
                    </Button> */}
                    <button
                      type="button"
                      className="Polaris-Button blue_button_ai"
                      onClick={handleOptimizeContent}
                      loading={isOptimizing}
                      disabled={
                        isAnyAiActionInProgress || !articleMetadata.content
                      }
                    >
                      <span
                        className="Polaris-Button__Content"
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: "1px",
                        }}
                      >
                        <span
                          className="Polaris-Button__Icon"
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <Icon source={WandIcon} color="white" />
                        </span>
                        <span className="Polaris-Text--root Polaris-Text--bodySm Polaris-Text--medium">
                          {isOptimizing
                            ? `Optimizing... ${Math.round(optimizeProgress)}%`
                            : "Optimize Content"}
                        </span>
                      </span>
                    </button>
                  </div>
                </div>
              </Card>

              {/* Show error message if optimization fails */}
              {optimizeError && (
                <Card>
                  <Text as="p" variant="bodySm" tone="critical">
                    {optimizeError}
                  </Text>
                </Card>
              )}

              {/* Headline Optimizer Section */}
              <Card background="bg-surface-secondary">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "16px",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <BlockStack gap="100">
                      <Text as="h4" variant="headingSm" fontWeight="semibold">
                        Headline Optimizer
                      </Text>
                      <Text as="p" variant="bodySm" tone="subdued">
                        Generate high-CTR alternative headlines
                      </Text>
                    </BlockStack>
                  </div>
                  <div>
                    {/* <Button
                      background="bg-fill-emphasis"
                      // variant="primary"
                      icon={<Icon source={LightbulbIcon} />}
                    >
                      Generate Headlines
                    </Button> */}
                    <button
                      type="button"
                      className="Polaris-Button blue_button_ai"
                      onClick={handleGenerateHeadlines}
                      loading={isGeneratingHeadlines}
                      disabled={
                        isAnyAiActionInProgress || !articleMetadata.title
                      }
                    >
                      <span
                        className="Polaris-Button__Content"
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: "1px",
                        }}
                      >
                        <span class="Polaris-Button__Icon">
                          <Icon source={LightbulbIcon} />
                        </span>
                        <span class="Polaris-Text--root Polaris-Text--bodySm Polaris-Text--medium">
                          {isGeneratingHeadlines
                            ? `Generating... ${Math.round(headlineProgress)}%`
                            : "Generate Headlines"}
                        </span>
                      </span>
                    </button>
                  </div>
                  {headlineError && (
                    <div style={{ marginTop: "8px" }}>
                      <Text variant="bodySm" tone="critical">
                        {headlineError}
                      </Text>
                    </div>
                  )}
                </div>
              </Card>

              {/* Content Expansion Ideas Section */}
              <Card background="bg-surface-secondary">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "16px",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <BlockStack gap="100">
                      <Text as="h4" variant="headingSm" fontWeight="semibold">
                        Content Expansion Ideas
                      </Text>
                      <Text as="p" variant="bodySm" tone="subdued">
                        AI suggests topics and expansion ideas based on article
                        performance
                      </Text>
                    </BlockStack>
                  </div>
                  <div>
                    <button
                      type="button"
                      className="Polaris-Button blue_button_ai"
                      onClick={handleGenerateIdeas}
                      loading={isGeneratingIdeas}
                      disabled={
                        isAnyAiActionInProgress || !articleMetadata.title
                      }
                    >
                      <span
                        className="Polaris-Button__Content"
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: "1px",
                        }}
                      >
                        <span class="Polaris-Button__Icon">
                          <Icon source={PlusIcon} />
                        </span>
                        <span class="Polaris-Text--root Polaris-Text--bodySm Polaris-Text--medium">
                          {isGeneratingIdeas
                            ? `Generating... ${Math.round(ideasProgress)}%`
                            : "Generate Ideas"}
                        </span>
                      </span>
                    </button>
                  </div>
                </div>
                {ideasError && (
                  <div style={{ marginTop: "12px" }}>
                    <Text variant="bodySm" tone="critical">
                      {ideasError}
                    </Text>
                  </div>
                )}
              </Card>
            </BlockStack>
          </Card>
        );

      case "topArticles":
        return (
          <Card>
            <BlockStack gap="400">
              <Text as="h3" variant="headingMd">
                Top Clicked Product
              </Text>
              {topProductsLoading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "40px",
                  }}
                >
                  <Spinner size="large" />
                </div>
              ) : Array.isArray(topProductsData) &&
                topProductsData.length > 0 ? (
                <DataTable
                  columnContentTypes={["text", "numeric", "numeric"]}
                  headings={[
                    <Text as="span" variant="bodySm" fontWeight="semibold">
                      PRODUCT NAME
                    </Text>,
                    createHeaderWithTooltip_top5article(
                      "CLICKS",
                      "Total product clicks generated from this article."
                    ),
                    createHeaderWithTooltip_top5article(
                      "CTR",
                      "Click-through rate (Clicks / Total Article Views)."
                    ),
                  ]}
                  rows={topProductsData.map((product) => {
                    // Get Total Views from metricsData (Index 0) safely
                    const totalViews =
                      metricsData && metricsData[0] ? metricsData[0].value : 0;

                    // Calculate CTR (Prevent division by zero)
                    // CTR = (product clicks / total article views) * 100, capped at 100%
                    const clicks = product.clicks || 0;
                    const rawCtr =
                      totalViews > 0 && clicks > 0
                        ? (clicks / totalViews) * 100
                        : 0;
                    const ctr = Math.min(rawCtr, 100).toFixed(2) + "%";

                    return [
                      // FIX: Use product.title (from backend response)
                      <Text as="span" variant="bodySm">
                        {product.title ||
                          product.product_handle ||
                          "Untitled Product"}
                      </Text>,
                      <Text as="span" variant="bodySm">
                        {clicks}
                      </Text>,
                      <Text as="span" variant="bodySm">
                        {ctr}
                      </Text>,
                    ];
                  })}
                />
              ) : (
                <div style={{ padding: "20px", textAlign: "center" }}>
                  <Text as="p" tone="subdued">
                    No clicked product available for this article.
                  </Text>
                </div>
              )}
            </BlockStack>
          </Card>
        );

      default:
        return null;
    }
  };

  const isAnyAiActionInProgress =
    isOptimizing || isGeneratingHeadlines || isGeneratingIdeas;

  const customStyles = `
    .Polaris-Spinner--sizeSmall svg {
      height: 1.1rem;
      width: 1.1em;
    }
    .dashboard-grid-container {
      position: relative;
      display: grid;
      grid-template-columns: repeat(12, 1fr);
      gap: 16px;
      margin: 24px 0;
    }

    .dashboard-grid-container > * {
      min-width: 0;
      width: 100%;
      transition: grid-column 200ms ease;
    }

    .sortable-card {
      transition: transform 200ms ease, opacity 200ms ease, box-shadow
200ms ease;
      will-change: transform;
    }

    .sortable-card[style*="translate"] {
      transition: none !important;
    }

    .sortable-card[data-dragging="true"] {
      transition: none !important;
      cursor: grabbing !important;
      overflow: hidden;
    }

    .sortable-card[data-dragging="true"]::after {
      content: "";
      position: absolute;
      inset: 0;
      border: 2px dashed #005bd3;
      border-radius: 8px;
      pointer-events: none;
      opacity: 0.4;
    }

    .sortable-card.is-edit {
      cursor: grab;
    }

    .sortable-card.is-edit > div {
      transition: transform 0.2s ease-out, box-shadow 0.2s ease-out;
    }

    .sortable-card.is-edit:hover > div {
      transform: translateY(-4px) scale(1.01);
      box-shadow: 0 8px 20px rgba(0, 91, 211, 0.15) !important;
    }

    .sortable-card.is-edit:active {
      cursor: grabbing !important;
    }

    .sortable-card.is-edit:active > div {
      transform: translateY(-2px) scale(1.005);
    }

    .sortable-card.is-edit * {
      pointer-events: none;
    }

    .sortable-card.is-edit {
      pointer-events: auto;
    }

    .Polaris-ShadowBevel {
      height: 100%;
    }

    .edit_dashboard_div {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      // margin-bottom: 20px;
    }

    .dashboard-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
    }


    @media (max-width: 450px){
      .dashboard-header{
        display: flex;
        flex-direction: column;
      }
      .edit_dashboard_div{
        margin-top: 15px;
        // justify-content: center;
      }
    }

    .filter_parent_div {
      margin-bottom: 24px;
    }


    /* Add to your CSS file or styled component */
    .progress-bar-container {
      width: 100%;
      height: 4px;
      background-color: #e0e0e0;
      border-radius: 2px;
      overflow: hidden;
      margin-top: 8px;
    }

    .progress-bar-fill {
      height: 100%;
      background: linear-gradient(90deg, #005BD3 0%, #0084FF 100%);
      transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      border-radius: 2px;
    }

    .progress-text {
      font-size: 12px;
      color: #6B7280;
      margin-top: 4px;
      transition: opacity 0.2s ease;
    }

    .filters-wrapper {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 16px;
      flex-wrap: wrap;
      gap: 16px;
    }

    .filters-controls {
      display: flex;
      align-items: center;
      gap: 24px;
      flex-wrap: wrap;
    }

    .filter-item {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .filter-item .Polaris-Select {
      min-width: 160px;
    }

    .date-range-picker .Polaris-Button {
      min-width: 180px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .Polaris-Select__Content {
      max-width: 160px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .Polaris-Select__SelectedOption {
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    /* DatePicker styles from attached file */
    .dp-with-header .Polaris-DatePicker__Title {
      display: none;
    }

    .dp-header {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 8px 12px;
    }

    .dp-label {
      font-weight: 600;
    }

    .Polaris-ButtonGroup {
      margin-left: 0px;
      margin-top: 6px;
      border: 1px solid black;
      width: fit-content;
      padding: 1px;
      border-radius: 10px;
    }

    .Polaris-Button--pressed {
      background-color: #000 !important;
      color: #fff !important;
      border-color: #000 !important;
    }

    .Polaris-Button--pressed:hover,
    .Polaris-Button--pressed:focus,
    .Polaris-Button--pressed:active {
      background-color: #000 !important;
      color: #fff !important;
      border-color: #000 !important;
    }

    .css-le0vmg {
      width: 110%;
      transform: translate(-88px, 0px);
    }

    .blue_button_ai {
      background-color: #005bd3 !important;
      color: #ffffff !important;
      border: none !important;
      border-radius: 6px;
      padding: 6px 12px 6px 6px;
      cursor: pointer;
      transition: none !important;
    }

    .blue_button_ai:hover,
    .blue_button_ai:focus,
    .blue_button_ai:active {
      background-color: #005bd3 !important;
      color: #ffffff !important;
      box-shadow: none !important;
    }

    .blue_button_ai:disabled {
      background-color: #b0b7c3 !important;
      color: #f5f5f5 !important;
      cursor: not-allowed !important;
      opacity: 1 !important;
    }


    @media (max-width: 600px) {
      .css-le0vmg {
        width: 125%;
        transform: translate(-90px, 0px);
      }
    }

    @media (max-width: 490px) {
      .css-le0vmg {
        width: 130%;
        transform: translate(-90px, 0px);
      }
    }

    @media (max-width: 425px) {
      .css-le0vmg {
        width: 137%;
        transform: translate(-90px, 0px);
      }
    }

    @media (max-width: 375px) {
      .css-le0vmg {
        width: 144%;
        transform: translate(-90px, 0px);
      }
    }

    @media (max-width: 767px) {
      .css-le0vmg {
        width: 120%;
        transform: translate(-90px, 0px);
      }
    }

    @media (max-width: 320px) {
      .css-le0vmg {
        width: 157%;
        transform: translate(-100px, 0px);
      }
    }


    @media (max-width: 768px) {
      .dashboard-grid-container {
        display: flex !important;
        flex-direction: column !important;
        gap: 16px;
      }

      .sortable-card {
        width: 100% !important;
        grid-column: span 12 !important;
      }

      .filter-item .date-range-picker .Polaris-Button {
        min-width: 140px;
      }
    }
  `;

  // Add this early return for initial loading
  if (isInitialLoading) {
    return (
      <Page>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "40px",
            marginLeft: "10px",
            marginRight: "10px",
          }}
        >
          <Card padding="600" roundedAbove="sm">
            <BlockStack gap="400" align="center">
              {/* App Logo */}
              <img
                src="https://analyticshero.io/static/analyticshero_loading.svg"
                style={{ width: 60, height: 60, margin: "auto" }}
              />

              {/* Title */}
              <Text variant="headingLg" alignment="center">
                Loading your article dashboard
              </Text>

              {/* Subtitle */}
              <Text variant="bodyMd" tone="subdued" alignment="center">
                We’re preparing your metrics and generating AI-powered insights.
              </Text>

              <Divider />

              <BlockStack gap="300">
                {/* Metrics Loaded */}
                <InlineStack align="center" gap="200">
                  {criticalDataLoaded.metrics ? (
                    <>
                      <div style={{ margin: 0 }}>
                        <Icon source={CheckIcon} tone="success" />
                      </div>
                      <Text>Metrics loaded</Text>
                    </>
                  ) : (
                    <>
                      <div style={{ margin: 0 }}>
                        <Icon source={ClockIcon} />
                      </div>
                      <Text tone="subdued">Loading metrics...</Text>
                    </>
                  )}
                </InlineStack>

                {/* Article Data Loaded */}
                <InlineStack align="center" gap="200">
                  {criticalDataLoaded.articles ? (
                    <>
                      <div style={{ margin: 0 }}>
                        <Icon source={CheckIcon} tone="success" />
                      </div>
                      <Text>Article data loaded</Text>
                    </>
                  ) : (
                    <>
                      <div style={{ margin: 0 }}>
                        <Icon source={ClockIcon} />
                      </div>
                      <Text tone="subdued">Loading articles...</Text>
                    </>
                  )}
                </InlineStack>

                {/* AI Insights */}
                <InlineStack align="center" gap="200">
                  {criticalDataLoaded.insights ? (
                    <>
                      <div style={{ margin: 0 }}>
                        <Icon source={CheckIcon} tone="success" />
                      </div>
                      <Text>AI insights generated</Text>
                    </>
                  ) : (
                    <>
                      <div style={{ margin: 0 }}>
                        <Icon source={ClockIcon} />
                      </div>
                      <Text tone="subdued">Generating AI insights...</Text>
                    </>
                  )}
                </InlineStack>
              </BlockStack>
            </BlockStack>
          </Card>
        </div>
      </Page>
    );
  }

  return (
    <div style={{ padding: "0px 10px 0px 10px" }}>
      {/* <Frame> */}
        <Page
          backAction={{
            content: "AnalyticsHero",
            url: `/dashboard/?shop=${encodeURIComponent(shopOrigin || "")}`,
          }}
        >
          <style>{customStyles}</style>

          <div className="dashboard-header">
            <div style={{ width: "100%" }}>
              <Text className variant="heading2xl" as="h2" alignment="start">
                <Link
                  to="https://apps.shopify.com/bloghero"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    textDecoration: "none",
                    color: "inherit", // keeps your heading color
                    cursor: "pointer",
                  }}
                >
                  <span>{articleMetadata.title}</span>
                </Link>
              </Text>

              {/* Updated status display logic */}
              <Text alignment="start" variant="bodyMd" as="p" tone="subdued">
                {articleMetadata.publishedDate ? (
                  <>
                    Published{" "}
                    {formatPublishedDate(articleMetadata.publishedDate)} by{" "}
                    <span style={{ textTransform: "capitalize" }}>
                      {articleMetadata.authorName}
                    </span>{" "}
                    in{" "}
                    <span
                      style={{
                        color: "rgba(0, 91, 211, 1)",
                        textTransform: "capitalize",
                      }}
                    >
                      {articleMetadata.categoryName}
                    </span>
                  </>
                ) : (
                  <>
                    Article is saved as draft by{" "}
                    <span style={{ textTransform: "capitalize" }}>
                      {articleMetadata.authorName}
                    </span>{" "}
                    in{" "}
                    <span
                      style={{
                        color: "rgba(0, 91, 211, 1)",
                        textTransform: "capitalize",
                      }}
                    >
                      {articleMetadata.categoryName}
                    </span>
                  </>
                )}
              </Text>
            </div>
            <div className="edit_dashboard_div" style={{ width: "100%" }}>
              <Link
                to={`https://${shopOrigin}/admin/articles/${articleId}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "none" }}
              >
                <Button variant="secondary">View Article</Button>
              </Link>
              <Button
                onClick={handleExportData}
                loading={isExporting}
                icon={ExportIcon}
                variant="primary"
              >
                Export Data
              </Button>

              <Button icon={EditIcon} onClick={() => setEditMode((v) => !v)}>
                {editMode ? "Done" : ""}
              </Button>

              {editMode && (
                <Button icon={ResetIcon} onClick={handleResetLayout}>
                  Reset
                </Button>
              )}
            </div>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Spinner size="large" />
              <Text variant="bodyMd" as="p" tone="subdued" alignment="center">
                Loading dashboard metrics...
              </Text>
            </div>
          )}

          {/* Error State */}
          {error && (
            <Card>
              <BlockStack gap="300">
                <Text as="p" variant="bodyMd" tone="critical">
                  Error: {error}
                </Text>
                <Button onClick={fetchDashboardMetrics}>Retry</Button>
              </BlockStack>
            </Card>
          )}

          {/* Dashboard Grid */}
          {!isLoading && !error && (
            <DndContext
              sensors={sensors}
              collisionDetection={customCollisionDetection}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={gridLayout.map((item) => item.id)}
                strategy={rectSortingStrategy}
              >
                <div className="dashboard-grid-container">
                  {gridLayout.map((item) => (
                    <SortableCard
                      key={item.id}
                      id={item.id}
                      editMode={editMode}
                      draggingHeight={draggingHeight}
                      style={{
                        gridColumn: `span ${
                          item.adjustedColSpan || item.colSpan
                        }`,
                      }}
                    >
                      {renderCard(item.id)}
                    </SortableCard>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
          <div style={{display: "none"}}>
            <Frame>
              {/* Add Toast at the end before closing Page */}
              {toastActive && (
                <Toast
                  content={toastContent}
                  error={toastError}
                  onDismiss={() => setToastActive(false)}
                  duration={3000}
                />
              )}
            </Frame>
          </div>
        </Page>
      {/* </Frame> */}
      {/* Add this at the end of your return statement, before </Page> */}
      <OptmisedContent
        open={showOptimizedModal}
        onClose={() => setShowOptimizedModal(false)}
        article={optimizedArticle}
        shopOrigin={shopOrigin}
      />
      <HeadlineModal
        open={showHeadlineModal}
        onClose={() => setShowHeadlineModal(false)}
        headlines={generatedHeadlines}
        onSelectHeadline={handleSelectHeadline}
        isGenerating={isGeneratingHeadlines}
      />
      <IdeasModal
        open={showIdeasModal}
        onClose={() => setShowIdeasModal(false)}
        ideas={contentIdeas}
        isGenerating={isGeneratingIdeas}
        // Add these two lines:
        onCreateArticle={handleCreateArticle}
        generatingIndex={generatingArticleIndex}
      />

      {/* Article Preview Modal */}
      <ArticlePreviewIdeasModal
        open={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          setPreviewArticle(null);
        }}
        article={previewArticle}
        isGenerating={generatingArticleIndex !== null}
        onSaveAsDraft={handleSaveAsDraft}
        isSaving={isSavingDraft}
      />
    </div>
  );
}

export default ArticleDetail;
